// View Mode (PRD bagian 13): watermark dinamis nama admin + waktu di seluruh layar, blokir klik
// kanan/salin/cetak, dan layar diburamkan saat jendela tidak aktif. Browser tidak bisa mencegah
// screenshot OS; watermark membuat setiap kebocoran bisa dilacak ke admin yang membukanya.
import { useEffect, useState, type ReactNode } from 'react';

const formatter = new Intl.DateTimeFormat('id-ID', {
  timeZone: 'Asia/Jakarta',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function useMinuteClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    // Selaraskan ke awal menit berikutnya, lalu perbarui tiap menit.
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(
      () => {
        setNow(new Date());
        interval = setInterval(() => setNow(new Date()), 60_000);
      },
      60_000 - (Date.now() % 60_000),
    );
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, []);
  return now;
}

export function ViewModeShield({ adminName, children }: { adminName: string; children: ReactNode }) {
  const now = useMinuteClock();
  const [blurred, setBlurred] = useState(false);
  const text = `${adminName} · ${formatter.format(now)} WIB · VIEW MODE`;

  useEffect(() => {
    const block = (event: Event) => event.preventDefault();
    const onKey = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && ['c', 'x', 'p', 's', 'a'].includes(key)) event.preventDefault();
      if (key === 'printscreen') setBlurred(true);
    };
    const hide = () => setBlurred(true);
    const show = () => setBlurred(false);
    const onVisibility = () => setBlurred(document.visibilityState !== 'visible');
    for (const type of ['contextmenu', 'copy', 'cut', 'dragstart']) document.addEventListener(type, block);
    document.addEventListener('keydown', onKey);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', hide);
    window.addEventListener('focus', show);
    window.addEventListener('beforeprint', hide);
    document.documentElement.classList.add('view-mode');
    return () => {
      for (const type of ['contextmenu', 'copy', 'cut', 'dragstart'])
        document.removeEventListener(type, block);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', hide);
      window.removeEventListener('focus', show);
      window.removeEventListener('beforeprint', hide);
      document.documentElement.classList.remove('view-mode');
    };
  }, []);

  return (
    <>
      <div
        data-testid="privacy-blur"
        data-blurred={blurred}
        className={blurred ? 'pointer-events-none blur-xl' : ''}
      >
        {children}
      </div>
      {blurred ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30"
          data-testid="privacy-overlay"
        >
          <p className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow">
            Layar disembunyikan saat jendela tidak aktif (View Mode)
          </p>
        </div>
      ) : null}
      <div
        aria-hidden
        data-testid="watermark"
        data-text={text}
        className="pointer-events-none fixed inset-0 z-40 overflow-hidden select-none"
      >
        <div className="absolute -inset-1/2 flex rotate-[-24deg] flex-wrap content-start gap-x-16 gap-y-20 p-8">
          {Array.from({ length: 120 }, (_, i) => (
            <span key={i} className="whitespace-nowrap text-sm font-semibold text-slate-900/10">
              {text}
            </span>
          ))}
        </div>
      </div>
    </>
  );
}
